import { globalConfigurationCache } from 'GlobalConfigurationCache';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { GlobalConfiguration } from '../models/GlobalConfiguration';

// 1. Define the feature‐flag keys as a union of your boolean props
export enum FeatureType {
  FeatureFinanceNewsArticles = 'featureFinanceNewsArticles',
  FeatureAiChat = 'featureAiChat',
  FeatureVeraGlobalChat = 'featureVeraGlobalChat',
  FeatureInvestmentEntities = 'featureInvestmentEntities',
  FeatureContentReviews = 'featureContentReviews',
  FeatureAdvisorAccess = 'featureAdvisorAccess',
}

// 2. Context value interface
interface FeatureContextType {
  hasFeature: (flag: FeatureType) => boolean;
  loading: boolean;
}

// 3. Create the context
const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

interface FeatureProviderProps {
  children: ReactNode;
}

// 4. Provider component
export const FeatureProvider: React.FC<FeatureProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<GlobalConfiguration | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // load (or re-use cached) config
    globalConfigurationCache
      .getConfig()
      .then((cfg) => {
        setConfig(cfg);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load global configuration:', error);
        setLoading(false);
      });
  }, []);

  const hasFeature = (featureType: FeatureType): boolean => {
    return Boolean(config && (config as any)[featureType]);
  };

  // You can choose to render a spinner or null while loading:
  if (loading) return null;

  return (
    <FeatureContext.Provider value={{ hasFeature, loading: false }}>
      {children}
    </FeatureContext.Provider>
  );
};

// 5. Hook to consume the context
export const useFeatures = (): FeatureContextType => {
  const ctx = useContext(FeatureContext);
  if (!ctx) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return ctx;
};

// 6. Gate component to wrap feature‐gated UI
interface FeatureGateProps {
  feature: FeatureType;
  children: ReactNode;
  fallback?: ReactNode;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, fallback = null }) => {
  const { hasFeature, loading } = useFeatures();

  if (loading) return null; // or return <LoadingSpinner /> or fallback

  return hasFeature(feature) ? <>{children}</> : <>{fallback}</>;
};
